import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Gavel as GavelIcon } from '@mui/icons-material';
import { authApi } from '../../api';
import { useAuth } from '../../hooks';

export interface TosAcceptanceModalProps {
  open: boolean;
}

/**
 * Modal that requires users to accept Terms of Service and Privacy Policy
 * This modal cannot be dismissed without accepting - it blocks all app usage
 */
export const TosAcceptanceModal: React.FC<TosAcceptanceModalProps> = ({ open }) => {
  const { refreshUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!accepted) return;

    setIsLoading(true);
    setError(null);

    try {
      await authApi.updateProfile({
        tosAcceptedAt: new Date().toISOString(),
      });
      // Refresh user data to update tosAcceptedAt in auth state
      // This closes the modal and allows the user to continue without losing app state
      await refreshUser();
      setIsLoading(false);
    } catch (_err) {
      setError('Failed to save your acceptance. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      // Prevent closing by clicking outside
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6" component="span">
            Terms of Service Update
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          We've updated our Terms of Service and Privacy Policy. Please review and accept them to continue using SquareUp.
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            By accepting, you acknowledge that you have read and agree to our:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <li>
              <Link component={RouterLink} to="/terms" target="_blank" rel="noopener">
                Terms of Service
              </Link>
              {' - '}
              <Typography variant="body2" component="span" color="text.secondary">
                Rules and guidelines for using SquareUp
              </Typography>
            </li>
            <li>
              <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
              {' - '}
              <Typography variant="body2" component="span" color="text.secondary">
                How we collect, use, and protect your data
              </Typography>
            </li>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
              disabled={isLoading}
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the Terms of Service and Privacy Policy
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!accepted || isLoading}
          fullWidth
        >
          {isLoading ? 'Saving...' : 'Accept and Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TosAcceptanceModal;
